import { Address } from "react-daum-postcode";
import { BuildingCommonFacilityDeskGroupModel, CommonFacilityModel, CommonFacilityType, DeskGroupModel } from "src/api/building/building-types";
import { MediaFile, MediaServiceType } from "src/api/public-types";
import { Tab } from "src/components";
import { numberToStringWithComma } from "src/utils/common-util";

// select box 공통 option
export type SelectOption = {
  value: string;
  label: string;
  index?: number;
  subOptions?: Array<SelectOption>;
};

// modal 공통
export type Modal = {
  isOpen: boolean;
  message?: string;
  type?: string;
  payload?: any;
  title?: string;
};

// esl 상세 모달
export type EslDatailModal = {
  isOpen: boolean;
  labelId?: string; // 조회할 label id
};

// 건물 기본정보 입력 form data
export type BasicInfoFormData = {
  buildingName: string;
  addressData: Address & { id?: string };
  zonecode: string;
  address: string;
  addressDetail: string;
  locationCode: string;
  description: string;
  feature: string;
  floorNums: number;
  undergroundFloorNums: number;
  completionYmd: string;
  remodelingYmd: string;
  buildingStructureTypes: Array<BuildingStructure>;
  buildingStructureType2List: Array<BuildingStructure>;
  leasableArea: number;
  leasableAreaPyeong?: string;
  leasableAreaNet?: number;
  leasableAreaNetPyeong?: string;
  rateOfUse?: string;
  exitDirection: string;
  entryStartTime: string;
  entryEndTime: string;
  isHoliday: boolean;
  setIsUsedRestoom: boolean;
  isUsedRestroom: boolean;
  restrooms: Array<Restroom>;
  buildingToilet2List: Array<Restroom>;
  isUsedElevator: boolean;
  passengerElev: string;
  freightElev: string;
  isUsedAirConditioning: boolean;
  heatingType: string;
  heatingTypeDesc: string;
  heatingFacilityType: string;
  isHeating: string;
  isHeatingEquipment: boolean;
  heatingFacilityTypeDesc: string;
  heatingFacilityTypeAddDesc: string;
  mapUrlNaver?: string;
  mapUrlKakao?: string;
  mapUrlT?: string;
  mapUrlGoogle?: string;
};

// 건축물 용도
export type BuildingStructure = {
  seq: number;
  id?: number;
  facility?: string; // 시설군
  use?: string | null; // 용도
  orderNums?: number;
};

// 화장실
export type Restroom = {
  seq: number;
  id?: number;
  floor: string; // floorOptions 의 value
  type: string; // restroomTypeOptions 의 value
  manCount?: number;
  womanCount?: number;
  count?: number;
  orderNums?: number;
};

// 프로퍼티
export type Property = {
  type: "FLOOR" | "HO";
  id?: number;
  floor?: string; // 층
  floorName?: string; // 층 명칭
  hoId?: number; // 호 id
  ho?: number; // 호
  hoName?: string; // 호 명칭
  dedicatedArea?: number; // 전용면적(m2)
  dedicatedAreaPyeong?: string; // 전용면적(평)
  contractArea?: number; // 임대면적(m2)
  contractAreaPyeong?: string; // 임대면적(평)
  rent?: number; // 임대료
  isGray?: boolean; // 테이블 row 색상 회색 여부 (층일 경우 회색)
  ownerId?: string;
  ownerName?: string;
};

// 좌석 공간
export type DeskSpace = {
  type: "GROUP" | "DESK";
  deskGroupId?: number; // 좌석 그룹 id
  deskId?: number; // 좌석 id
  floor?: string; // 위치. 층
  deskGroupName?: string; // 좌석 그룹 명
  deskGroupDescription?: string; // 설명
  deskChartDescription?: string; // 좌석배치도 설명
  deskName?: string; // 좌석 명
  locationCode?: string; // 좌석 location code
  centerX?: string; // 좌석배치도 X 값
  centerY?: string; // 좌석배치도 Y 값
  isGray?: boolean; // 회색 row 여부
  mediaList?: MediaFile[];
};

// 프론트에서 사용할 좌석 공간
export type FrontDeskSpace = {
  type: "GROUP" | "DESK";
  deskGroupId?: number; // 좌석 그룹 id
  deskId?: number; // 좌석 id
  floor?: string; // 위치. 층
  deskGroupName?: string; // 좌석 그룹 명
  deskGroupDescription?: string; // 설명
  deskChartDescription?: string; // 좌석배치도 설명
  deskName?: string; // 좌석 명
  locationCode?: string; // 좌석 location code
  centerX?: string; // 좌석배치도 X 값
  centerY?: string; // 좌석배치도 Y 값
  isGray?: boolean; // 회색 row 여부
  originalGroupId?: number;
};

/*
  건물 회의실/좌석/편의시설
 */
export interface CommonFacility extends CommonFacilityModel {
  eslLabelCode?: string; // ESL 라벨 코드
  eslLabel?: string; // ESL 매핑주소 {id}-{code}
}

// 건축물 용도 시설군
export const buildingStructureOptions: Array<SelectOption> = [
  {
    value: "BUILDING_STRUCTURE_CAR",
    label: "자동차관련시설군",
    // 용도
    subOptions: [{ value: "BUILDING_STRUCTURE_CAR_1", label: "자동차관련시설" }],
  },
  {
    value: "BUILDING_STRUCTURE_INDUSTRY",
    label: "산업등의시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_INDUSTRY_1", label: "운수시설" },
      { value: "BUILDING_STRUCTURE_INDUSTRY_2", label: "창고시설" },
      { value: "BUILDING_STRUCTURE_INDUSTRY_3", label: "공장" },
      { value: "BUILDING_STRUCTURE_INDUSTRY_4", label: "위험물저장및처리시설" },
      { value: "BUILDING_STRUCTURE_INDUSTRY_5", label: "쓰레기처리시설" },
      { value: "BUILDING_STRUCTURE_INDUSTRY_6", label: "묘지관련시설" },
      { value: "BUILDING_STRUCTURE_INDUSTRY_7", label: "장례식장" },
    ],
  },
  {
    value: "BUILDING_STRUCTURE_TELECOM",
    label: "전기, 통신 시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_TELECOM_1", label: "방송통신시설" },
      { value: "BUILDING_STRUCTURE_TELECOM_2", label: "발전시설" },
    ],
  },
  {
    value: "BUILDING_STRUCTURE_CULTURE",
    label: "문화 및 집회 시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_CULTURE_1", label: "문화및집회시설" },
      { value: "BUILDING_STRUCTURE_CULTURE_2", label: "종교시설" },
      { value: "BUILDING_STRUCTURE_CULTURE_3", label: "위락시설" },
      { value: "BUILDING_STRUCTURE_CULTURE_4", label: "관광휴게시설" },
    ],
  },
  {
    value: "BUILDING_STRUCTURE_BUSINESS",
    label: "영업 시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_BUSINESS_1", label: "판매시설" },
      { value: "BUILDING_STRUCTURE_BUSINESS_2", label: "운동시설" },
      { value: "BUILDING_STRUCTURE_BUSINESS_3", label: "숙박시설" },
      { value: "BUILDING_STRUCTURE_BUSINESS_4", label: "고시원" },
    ],
  },
  {
    value: "BUILDING_STRUCTURE_EDU_WELFARE",
    label: "교육 및 복지 시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_EDU_WELFARE_1", label: "의료시설" },
      { value: "BUILDING_STRUCTURE_EDU_WELFARE_2", label: "노유자시설" },
      { value: "BUILDING_STRUCTURE_EDU_WELFARE_3", label: "교육연구시설" },
      { value: "BUILDING_STRUCTURE_EDU_WELFARE_4", label: "수련시설" },
    ],
  },
  {
    value: "BUILDING_STRUCTURE_COMMUNITY",
    label: "근린생활 시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_COMUNITY_1", label: "제1종근린생활시설" },
      { value: "BUILDING_STRUCTURE_COMUNITY_2", label: "제2종근린생활시설" },
    ],
  },
  {
    value: "BUILDING_STRUCTURE_OFFICIAL",
    label: "주거업무 시설군",
    subOptions: [
      { value: "BUILDING_STRUCTURE_OFFICIAL_1", label: "단독주택" },
      { value: "BUILDING_STRUCTURE_OFFICIAL_2", label: "공동주택" },
      { value: "BUILDING_STRUCTURE_OFFICIAL_3", label: "업무시설" },
      { value: "BUILDING_STRUCTURE_OFFICIAL_4", label: "교정및군사시설" },
    ],
  },
];

// 주 출입구 방향
export const exitDirectionOptions: Array<SelectOption> = [
  { value: "PRODUCT_DIRECTION_E", label: "동" },
  { value: "PRODUCT_DIRECTION_W", label: "서" },
  { value: "PRODUCT_DIRECTION_S", label: "남" },
  { value: "PRODUCT_DIRECTION_N", label: "북" },
  { value: "PRODUCT_DIRECTION_SE", label: "남동" },
  { value: "PRODUCT_DIRECTION_SW", label: "남서" },
  { value: "PRODUCT_DIRECTION_NE", label: "북동" },
  { value: "PRODUCT_DIRECTION_NW", label: "북서" },
];

/**
 * 숫자 옵션 생성 (from ~ to)
 * @param from 시작 숫자
 * @param to 끝 숫자
 * @returns SelectOption 배열
 */
const generateNumericOptions = (from: number, to: number): Array<SelectOption> =>
  Array.from({ length: to - from + 1 }, (_, i) => {
    const v = String(from + i);
    return { value: v, label: v };
  });

// ── 층수 ──
export const MAX_ABOVE_GROUND_FLOOR = 200;
export const MAX_UNDERGROUND_FLOOR = 20;

export const buildingFloorOptions: Array<SelectOption> = generateNumericOptions(1, MAX_ABOVE_GROUND_FLOOR);
export const buildingUndergroundFloorOptions: Array<SelectOption> = generateNumericOptions(1, MAX_UNDERGROUND_FLOOR);

export const aboveGroundFloorOptions: Array<SelectOption> = Array.from({ length: MAX_ABOVE_GROUND_FLOOR }, (_, i) => ({
  value: String(MAX_ABOVE_GROUND_FLOOR - i),
  label: `지상 ${MAX_ABOVE_GROUND_FLOOR - i}층`,
}));
export const undergroundFloorOptions: Array<SelectOption> = Array.from({ length: MAX_UNDERGROUND_FLOOR }, (_, i) => ({
  value: String(-(i + 1)),
  label: `지하 ${i + 1}층`,
}));
export const floorOptions: Array<SelectOption> = [...aboveGroundFloorOptions, ...undergroundFloorOptions];

// ── 화장실 ──
export const MAX_RESTROOM_COUNT = 10;

export const restroomFloorOptions: Array<SelectOption> = floorOptions.map((option) => {
  const num = Number(option.value);
  return {
    value: num < 0 ? `UNDERGROUND_FLOOR_${Math.abs(num)}` : `FLOOR_${num}`,
    label: option.label,
  };
});
export const restroomTypeOptions: Array<SelectOption> = [
  { value: "PUBLIC", label: "남여 공용" },
  { value: "SEPARATION", label: "남여 분리" },
];
export const restroomCountOptions: Array<SelectOption> = generateNumericOptions(1, MAX_RESTROOM_COUNT);

// ── 엘리베이터 ──
export const MAX_PASSENGER_ELEV = 10;
export const MAX_FREIGHT_ELEV = 10;

export const passengerElevOptions: Array<SelectOption> = generateNumericOptions(1, MAX_PASSENGER_ELEV);
export const freightElevOptions: Array<SelectOption> = generateNumericOptions(1, MAX_FREIGHT_ELEV);

// ── 냉난방 ──
export const heatingTypeOptions: Array<SelectOption> = [
  { value: "CENTRAL_AIR_CONDITIONER", label: "중앙냉난방" },
  { value: "INDIVIDUAL_AIR_CONDITIONER", label: "개별냉난방" },
  { value: "DIRECT_HEATING_AIR_CONDITIONER", label: "직접입력" },
];
export const isHeatingOptions: Array<SelectOption> = [
  { value: "true", label: "제공" },
  { value: "false", label: "제공안함" },
];
export const heatingFacilityTypeOptions: Array<SelectOption> = [
  { value: "CEILING_AIR_CONDITIONER", label: "냉난방기(천장형)" },
  { value: "STAND_UP_AIR_CONDITIONER", label: "냉난방기(거치형)" },
  { value: "DIRECT_FACILITY_AIR_CONDITIONER", label: "직접입력" },
];

// ── 주차 ──
export const MAX_PARKING_LOT = 10;

export const parkingLotNumOptions: Array<SelectOption> = generateNumericOptions(0, MAX_PARKING_LOT);

// 층 호 구분
export const floorHoTypeOptions: Array<SelectOption> = [
  { value: "FLOOR", label: "층" },
  { value: "HO", label: "호" },
];

// 좌석 구분
export const deskSpaceTypeOptions: Array<SelectOption> = [
  { value: "GROUP", label: "좌석 그룹" },
  { value: "DESK", label: "좌석" },
];

// 공간유형
export const spaceStyleOptions: Array<SelectOption> = [
  // { value: "SPACE_STYLE_UNRECOGNIZED", label: "선택해주세요." },
  { value: "ROOM", label: "Rooms" },
  { value: "CONTAINER", label: "Container" },
];

// floor 하위 모든 호실의 target 값을 더한값을 리턴한다
export function sumByFloor(floor: string, target: string, data: Array<Property>, format: string = "0,0.00") {
  const hoList = (data || []).filter((d: Property) => d.type === "HO" && d.floor === floor);
  const sum = hoList.reduce((acc: number, cur: any) => acc + Number(cur[target] || 0), 0);
  return numberToStringWithComma(sum, format);
}

// 프로퍼티 정렬. (층 부터 정렬 후 호 정렬)
export function sortingProperties(properties: Array<Property>) {
  return properties.sort((a: Property, b: Property) => {
    const floor1 = Number(a.floor);
    const floor2 = Number(b.floor);
    const ho1 = Math.abs(Number(a.ho || 0));
    const ho2 = Math.abs(Number(b.ho || 0));
    if (floor1 > floor2) return -1;
    if (floor1 < floor2) return 1;
    if (ho1 < ho2) return -1;
    if (ho1 > ho2) return 1;
    return 0;
  });
}

// 회의실 정렬.
export function sortingBuildingFacilities(facilities: Array<CommonFacilityModel>) {
  return facilities.sort((a: CommonFacilityModel, b: CommonFacilityModel) => {
    const isGroundA = a.isGround; // 지상여부
    const floorNumA = a.floorNum; // 위치
    const floorA = Number(`${isGroundA ? "" : "-"}${floorNumA}`);
    const isGroundB = b.isGround; // 지상여부
    const floorNumB = b.floorNum; // 위치
    const floorB = Number(`${isGroundB ? "" : "-"}${floorNumB}`);
    if (floorA > floorB) return -1;
    if (floorA < floorB) return 1;
    return 0;
  });
}

// 좌석 그룹 정렬.
export function sortingDeskGroupList(deskGroupList: Array<BuildingCommonFacilityDeskGroupModel>) {
  return deskGroupList.sort((a: BuildingCommonFacilityDeskGroupModel, b: BuildingCommonFacilityDeskGroupModel) => {
    const isGroundA = a.isGround; // 지상여부
    const floorNumA = a.floorNum; // 층
    const floorA = Number(`${isGroundA ? "" : "-"}${floorNumA}`);
    const isGroundB = b.isGround; // 지상여부
    const floorNumB = b.floorNum; // 층
    const floorB = Number(`${isGroundB ? "" : "-"}${floorNumB}`);
    if (floorA > floorB) return -1;
    if (floorA < floorB) return 1;
    if (Number(a.id || 0) < Number(b.id || 0)) return -1;
    if (Number(a.id || 0) > Number(b.id || 0)) return 1;
    return 0;
  });
}

// 좌석 그룹 정렬. (좌석배치도에서 사용)
export function sortingDeskGroupLayoutList(deskGroupList: Array<DeskGroupModel>) {
  return deskGroupList.sort((a: DeskGroupModel, b: DeskGroupModel) => {
    const isGroundA = a.isGround; // 지상여부
    const floorNumA = a.floorNum; // 층
    const floorA = Number(`${isGroundA ? "" : "-"}${floorNumA}`);
    const isGroundB = b.isGround; // 지상여부
    const floorNumB = b.floorNum; // 층
    const floorB = Number(`${isGroundB ? "" : "-"}${floorNumB}`);
    if (floorA > floorB) return -1;
    if (floorA < floorB) return 1;
    if (Number(a.id || 0) < Number(b.id || 0)) return -1;
    if (Number(a.id || 0) > Number(b.id || 0)) return 1;
    return 0;
  });
}

// 편의시설 정렬.
export function sortingRefreshRoomList(refreshRoomList: Array<CommonFacilityModel>) {
  return refreshRoomList.sort((a: CommonFacilityModel, b: CommonFacilityModel) => {
    const isGroundA = a.isGround; // 지상여부
    const floorNumA = a.floorNum; // 위치
    const floorA = Number(`${isGroundA ? "" : "-"}${floorNumA}`);
    const isGroundB = b.isGround; // 지상여부
    const floorNumB = b.floorNum; // 위치
    const floorB = Number(`${isGroundB ? "" : "-"}${floorNumB}`);
    if (floorA < floorB) return -1;
    if (floorA > floorB) return 1;
    return 0;
  });
}

// 건물 이미지 리스트 전체 불러오기

// export const buildingMediaServiceType = "MEDIA_BUILDING,MEDIA_BUILDING_ETC";
export const buildingMediaServiceType: Array<MediaServiceType> = [
  "MEDIA_BUILDING",
  "MEDIA_BUILDING_INSIDE",
  "MEDIA_BUILDING_FLOOR_ROOM",
  "MEDIA_BUILDING_COMMONFACILITY",
  "MEDIA_BUILDING_ETC",
];

// 좌석 아이템
export type DeskItem = {
  id: string;
  name: string;
  left: number;
  top: number;
};

// 관리처 CRUD 처리요청
export const CmdType = {
  C: "C", // 등록
  U: "U", // 수정
  D: "D", // 삭제
} as const;

export type UnionCmdType = (typeof CmdType)[keyof typeof CmdType];

// 관리처 역할 타입
export const RoleType = {
  UNRECOGNIZED: "ROLE_UNRECOGNIZED",
  MANAGER: "ROLE_MANAGER",
  USER: "ROLE_USER",
} as const;

export type UnionRoleType = (typeof RoleType)[keyof typeof RoleType];

// 관리자 업무타입
export const OfficerType = {
  UNRECOGNIZED: "OFFICER_UNRECOGNIZED", // 정의 안된
  MANAGEMENT: "MANAGEMENT", // 관리
  FACILITY: "FACILITY", // 시설
  CLEANER: "CLEANER", // 미화
  SECURITY: "SECURITY", // 보안과 경비
  CONVENIENCE: "CONVENIENCE", // 생활편의
  CS: "CS", // CS
} as const;

export type UnionOfficerType = (typeof OfficerType)[keyof typeof OfficerType];

export type SubTabType = { value: CommonFacilityType; label: string };

export const facilityTabs: SubTabType[] = [
  { value: "PARKING", label: "주차" },
  { value: "MEETING_ROOM", label: "회의실" },
  { value: "DESK", label: "좌석" },
  { value: "REFRESH_ROOM", label: "편의시설" },
];

type ModalType = "FACILITY_DELETE" | "FACILITY_UPDATE" | "FACILITY_CREATE";

export type FacilityModal = Omit<Modal, "type"> & {
  type?: ModalType;
};

export const facilitySubTabs: Array<Tab> = [
  { value: "basicInfo", label: "기본정보" },
  { value: "accessGroup", label: "출입그룹" },
];
